import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material"
import { useState } from "react"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { DocumentAnalyzedObject, loadModulesForACM, LoadModulesForACMPayload } from "../documentQuestionsApi"


export interface LoadModulesForACMDialogProps {
    documentId: AppId
    sectionId: string
    onSuccessfulSave: (analysedObjects: DocumentAnalyzedObject[]) => void
    onCancelClick: () => void,
}

export const LoadModulesForACMDialog = ({ documentId, sectionId, onCancelClick, onSuccessfulSave }: LoadModulesForACMDialogProps) => {
    const dispatch = useAppDispatch()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [validationErrors, setValidationErrors] = useState(false)
    const [overwriteExisting, setOverwriteExisting] = useState(false)

    const handleOkClick = async () => {
        setValidationErrors(true)
        setSaving(true);
        try {
            const params: LoadModulesForACMPayload = {
                overwriteExisting,
            };
            const result = await dispatch(loadModulesForACM({ documentId, sectionId, params: params }));

            if (loadModulesForACM.fulfilled.match(result)) {
                const analysedObjects = result.payload as DocumentAnalyzedObject[];
                dispatch(showSuccess("saved"));
                onSuccessfulSave(analysedObjects);
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }

        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };


    return <>
        <Dialog key="load" open={true} maxWidth="xs" fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Localized id="document-analyzed-objects-load-modules">
                    <span>_load</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers>
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        alignItems: "center",
                        ".MuiAlert-message": { width: "100%" },
                    }}
                >
                    <Localized id="document-analyzed-objects-load-modules-desc">
                        <span>_load_desc</span>
                    </Localized>
                </Alert>
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={overwriteExisting}
                                onChange={(e) => setOverwriteExisting(e.target.checked)}
                            />
                        }
                        label={
                            <Localized id="document-analyzed-objects-overwrite-current-modules">
                                <span>_Nadpisz bieżące moduły</span>
                            </Localized>
                        }
                    />
                </Box>

                {serverError && (
                    <Alert sx={{ marginTop: 1 }} severity="error">
                        <Localized id={serverError}>
                            <span style={{ whiteSpace: 'pre-line' }}>_{serverError}</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton loading={saving} onClick={handleOkClick}></SaveButton>
                <CancelButton loading={saving} onClick={onCancelClick}></CancelButton>
            </DialogActions>
        </Dialog>
    </>
}
